import React, { useRef, useState } from "react";
import { ChevronDown, RefreshCw } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { PROJECTION_OWNERS } from "../projectionContract";
import {
  RECONCILIATION_REQUEST_STATUSES,
  captureReconciliationRequest,
  createReconciliationRequestRegistry,
  resolveReconciliationOutcome,
} from "../reconciliationComposition";

/**
 * DEVELOPMENT-ONLY. The IP-008/ENG-009 status and quarantine controls,
 * moved here verbatim from `WorkspaceOperationalRow` by AW-VIS-2.
 *
 * WHY THEY MOVED
 * --------------
 * The operational row is now shared with Production (see that file's
 * docstring). These two are not, and must not be: `StatusActionControl`
 * is locally inert by construction, and `QuarantineNotice` drives a
 * fake, in-memory ENG-009 reconciliation registry through
 * `projectionContract` and `reconciliationComposition`. Importing
 * either into Production would both cross the fixture firewall and
 * present a status affordance that the real shell already implements
 * properly with Start Dialing, Pause, Resume and Logout Campaign.
 *
 * Nothing here changed. Same elements, same testids, same copy, same
 * behaviour -- only the file. `AgentWorkspaceShell` composes them into
 * the row's `controls` and `notice` slots.
 */

// Maps a presentation tone to the nearest native MissionAssist Button
// variant (see components/ui/button.jsx). "default" is the same variant
// the Leads page "Add Leads" button uses (bg-indigo-500/90 ...) and is the
// baseline for prominent, in-progress operational states; "success" and
// "destructive" are reserved for the two confirmed extremes. Only the
// truly non-participating state (Logged Out) uses the muted "secondary"
// treatment.
const STATUS_BUTTON_VARIANT = Object.freeze({
  success: "success",
  critical: "destructive",
  warning: "default",
  info: "default",
  pending: "default",
  neutral: "secondary",
});

// ENG-009: only the telephony-owner-evidence-driven quarantine reasons have
// a modeled reconciliation-request path in this fake environment — the
// coarse telephony projection is the one owner-evidence source Unknown/
// reconciliation-required is composed from (workspaceViewModel.js). Unknown
// authorization or controller-fence evidence is surfaced as quarantine too,
// but resolves via a fresh decision (ENG-005/ENG-006's own existing
// semantics), not a reconciliation request — so no request affordance is
// offered for those reasons.
const RECONCILIATION_ELIGIBLE_REASONS = new Set(["telephony-evidence-unknown", "reconciliation-required"]);

const QUARANTINE_COPY = Object.freeze({
  "telephony-evidence-unknown":
    "Required evidence is currently indeterminate. The status control is quarantined until reconciled.",
  "reconciliation-required":
    "Telephony evidence requires reconciliation before the status control can resume.",
  reconnecting: "Recovery is in progress. The status control remains quarantined until authority is restored.",
  "authorization-unknown": "Authorization evidence is indeterminate. The status control is quarantined.",
  "controller-fence-unknown": "Controller evidence is indeterminate. The status control is quarantined.",
});

/**
 * The current Workspace status. This is the control the agent will use to
 * change operational status in a future implementation packet — it must
 * read as an action, not a passive badge. Under IP-008 it remains locally
 * inert: no provider effect, session mutation, or API call is dispatched.
 */
export function StatusActionControl({ label, tone }) {
  const variant = STATUS_BUTTON_VARIANT[tone] || "secondary";
  const descriptionId = "workspace-status-control-description";

  return (
    <>
      <Button
        type="button"
        variant={variant}
        aria-haspopup="true"
        aria-describedby={descriptionId}
        title="Change status (unavailable in this preview)"
        data-testid="workspace-status"
      >
        {label}
        <ChevronDown className="h-4 w-4 opacity-80" aria-hidden="true" />
      </Button>
      <span id={descriptionId} className="sr-only">
        Opens status selection. Unavailable in this preview.
      </span>
    </>
  );
}

/**
 * ENG-009 Unknown & Recovery: presents dependent-control quarantine and the
 * fake, in-memory, idempotent reconciliation-request capture/resolution
 * flow for the one owner-evidence source (telephony) that drives it.
 *
 * The capture registry and active-request state are local to this
 * component's own mount (useRef/useState), so captured, unresolved context
 * clears the same way the rest of post-login Workspace state already does
 * — this component unmounts with the whole post-login section on logout,
 * and remounts clean (AgentWorkspaceShell.jsx; see the pre-existing "state
 * resets on remount" test). No Account-switch affordance exists yet in
 * this Boundary A shell, so remount is the available teardown boundary.
 *
 * Capturing and resolving never reference viewModel.attempt, intent, or
 * captureSemanticIntent/evaluateAttemptAdmission — this component has no
 * import of those, so it cannot replay a prior captured intent by
 * construction (ENG-009 §4 "Unknown does not authorize blind retry,
 * command replay, or optimistic success"). Resolution is a pure function
 * of the captured request and the CURRENT telephony evidence.kind only,
 * recomputed on every render — no timer, poll, or async delay simulates
 * background provider behavior.
 */
export function QuarantineNotice({ quarantine, viewModel }) {
  const registryRef = useRef(null);
  // `== null` rather than `!current`: behaviourally identical for a
  // registry object, and the form `react-hooks/refs` recognises as a
  // once-only initialisation. The predecessor used `!current` and
  // carried a standing lint error on main; moving the code was the
  // moment to stop carrying it.
  if (registryRef.current == null) registryRef.current = createReconciliationRequestRegistry();
  const [activeRequest, setActiveRequest] = useState(null);

  if (!quarantine.quarantined) return null;

  const telephonyProjection = viewModel.workspaceState.projections?.telephony;
  const eligibleForReconciliation =
    RECONCILIATION_ELIGIBLE_REASONS.has(quarantine.reason) && Boolean(telephonyProjection);

  function handleRequestReconciliation() {
    if (!telephonyProjection) return;
    const captured = captureReconciliationRequest({
      accountId: viewModel.accountId,
      subjectOwner: PROJECTION_OWNERS.TELEPHONY,
      subjectProjectionId: telephonyProjection.projectionId,
      subjectRevision: telephonyProjection.revision,
      registry: registryRef.current,
      capturedAt: viewModel.logicalNow,
    });
    setActiveRequest(captured);
  }

  const resolved =
    activeRequest && telephonyProjection
      ? resolveReconciliationOutcome({
          request: activeRequest,
          subjectEvidenceKind: telephonyProjection.evidence.kind,
          resolvedAt: viewModel.logicalNow,
        })
      : null;

  return (
    <div
      className="flex w-full flex-wrap items-center gap-3 rounded-lg border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
      role="status"
      data-testid="workspace-quarantine-notice"
      data-quarantine-reason={quarantine.reason}
    >
      <span data-testid="workspace-quarantine-reason">
        {QUARANTINE_COPY[quarantine.reason] || "The status control is quarantined pending reconciliation."}
      </span>

      {eligibleForReconciliation ? (
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-7 gap-1 rounded-full px-2.5 text-[11px]"
            onClick={handleRequestReconciliation}
            disabled={Boolean(activeRequest)}
            data-testid="workspace-request-reconciliation-button"
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            {activeRequest ? "Reconciliation Requested" : "Request Reconciliation"}
          </Button>

          {resolved ? (
            <span
              data-testid={`workspace-reconciliation-${resolved.status}`}
              className="text-[11px] text-amber-200"
            >
              {resolved.status === RECONCILIATION_REQUEST_STATUSES.RESOLVED
                ? "Reconciliation resolved by the owning domain."
                : "Reconciliation request captured; still awaiting owner resolution."}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
